// Route Code Added 10/14/26
/*
 * Route — Cart Drawer / Protection Modal Compatibility Handler
 *
 * Purpose
 *   Keeps the cart drawer open AND intact while a shopper opens and closes the
 *   Route package-protection details modal ("See Details"). Without this handler,
 *   closing that modal (its close button, the Esc key, or a click on the dimmed
 *   background) breaks the cart drawer.
 *
 * Background (Archetype "Impulse" theme)
 *   1) The Route modal renders at the end of <body>, OUTSIDE the cart drawer. The
 *      theme's drawer (a theme.Drawers instance) closes itself on two triggers
 *      bound to the window: a click outside the drawer, and the Esc key. Because
 *      the Route modal lives outside the drawer, dismissing it fires those
 *      triggers and the drawer closes too.
 *   2) The theme ALSO uses a single shared class on <html>, "js-drawer-open", for
 *      whichever drawer is open (cart OR search). That class drives the
 *      ".main-content::after" backdrop and the page scroll-lock. The theme's
 *      Predictive Search registers its OWN document-level Esc handler
 *      (PredictiveSearch.close -> closeSearchDrawer) that strips "js-drawer-open"
 *      on every Esc, even when search was never opened.
 *
 *   So on Esc: the Route modal closes, this handler keeps the cart drawer's own
 *   close from firing (point 1), but Predictive Search still strips the shared
 *   "js-drawer-open" (point 2) — leaving the cart drawer visually open yet with no
 *   backdrop, no scroll-lock, and an empty/white appearance. (The X and backdrop
 *   vectors don't trigger Predictive Search's Esc handler, so they were fine.)
 *
 * How this handler works
 *   A) Wraps theme.Drawers.prototype.close (cart drawer only) and ignores a close
 *      that was triggered by (i) a click on the Route modal, or (ii) Esc pressed
 *      while the Route modal was open (a short keydown-time shield — needed because
 *      the modal closes first, so by the time the drawer's Esc handler runs the
 *      modal is already gone).
 *   B) After an Esc that closed the Route modal, if the cart drawer is still open,
 *      restores the shared "js-drawer-open" class that Predictive Search stripped
 *      (and clears the transient "js-drawer-closing"), so the backdrop and
 *      scroll-lock come back and the drawer renders normally.
 *
 *   Every other close — Esc with no modal, click outside the drawer, the drawer's
 *   own close button — is left completely unchanged. A second Esc (modal already
 *   closed) closes the drawer normally.
 *
 * Scope / safety
 *   - Acts only on the cart drawer (id "CartDrawer"); the nav/search drawers are
 *     untouched, and the class is only restored while the cart drawer is still open.
 *   - Only intervenes while/just-after the Route modal (.route-modal) is involved.
 *   - Wrapped in try/catch; if anything is unexpected it falls through to the
 *     theme's original behavior. If theme.Drawers is never present, nothing is
 *     patched and the drawer behaves exactly as it does today.
 *   - Idempotent: patches the prototype at most once.
 *
 * Verified on the draft theme (Impulse, App Embed v3 / Snappy): closing the modal
 * with Esc, the close button, or a backdrop click leaves the cart drawer open,
 * fully rendered, with its backdrop and scroll-lock intact; a second Esc closes
 * the drawer normally; Esc/click-outside with no modal is unchanged.
 *
 * Please contact Route support before modifying or removing this file.
 */
(function () {
  'use strict';

  var MODAL_OPEN_SELECTOR = '.route-modal.rm-open'; // Route-owned; modal is open
  var MODAL_SELECTOR      = '.route-modal';         // Route-owned; modal container/backdrop
  var DRAWER_ID           = 'CartDrawer';           // THEME-SPECIFIC — the cart drawer element id
  var DRAWER_OPEN_CLASS   = 'js-drawer-open';       // THEME-SPECIFIC — shared <html> "a drawer is open" class
  var DRAWER_CLOSING_CLASS= 'js-drawer-closing';    // THEME-SPECIFIC — transient closing class added by search close
  var ESC_SHIELD_MS       = 1200;                   // window after Esc-keydown to ignore the drawer's Esc-close

  var shieldUntil = 0;

  function cartDrawerStillOpen() {
    try { return !!document.querySelector('#' + DRAWER_ID + '.drawer--is-open'); }
    catch (err) { return false; }
  }

  // Re-apply the shared drawer-open state that Predictive Search's Esc handler
  // strips, but only while the cart drawer is actually still open.
  function restoreDrawerOpenState() {
    try {
      if (!cartDrawerStillOpen()) return;
      var root = document.documentElement;
      if (!root.classList.contains(DRAWER_OPEN_CLASS)) root.classList.add(DRAWER_OPEN_CLASS);
      if (root.classList.contains(DRAWER_CLOSING_CLASS)) root.classList.remove(DRAWER_CLOSING_CLASS);
    } catch (err) { /* no-op */ }
  }

  // On Esc while the Route modal is open: (1) shield the drawer's Esc-close, and
  // (2) schedule a restore of the shared class after the theme's own Esc handlers
  // (including Predictive Search) have run. Restore is idempotent, so running it a
  // few times across the handler window is safe and covers timing differences.
  try {
    document.addEventListener('keydown', function (e) {
      try {
        if ((e.key === 'Escape' || e.keyCode === 27) &&
            document.querySelector(MODAL_OPEN_SELECTOR)) {
          shieldUntil = Date.now() + ESC_SHIELD_MS;
          if (window.requestAnimationFrame) requestAnimationFrame(restoreDrawerOpenState);
          setTimeout(restoreDrawerOpenState, 0);
          setTimeout(restoreDrawerOpenState, 60);
          setTimeout(restoreDrawerOpenState, 200);
        }
      } catch (err) { /* no-op */ }
    }, true); // capture, so the shield is set before the theme's handlers run
  } catch (err) { /* no-op: leave behavior unchanged */ }

  function isCartDrawer(instance) {
    try {
      var d = instance && instance.drawer;      // jQuery object or DOM node
      var node = d ? (d[0] || d) : null;
      return !!node && node.id === DRAWER_ID;
    } catch (err) { return false; }
  }

  function patch() {
    try {
      if (!window.theme || !theme.Drawers || !theme.Drawers.prototype) return false;
      var proto = theme.Drawers.prototype;
      if (proto.__routeModalPatch) return true; // already patched
      var originalClose = proto.close;

      proto.close = function (evt) {
        try {
          if (isCartDrawer(this)) {
            // 1) Close triggered by a click on the Route modal (X or backdrop).
            if (evt && evt.target && evt.target.closest &&
                evt.target.closest(MODAL_SELECTOR)) {
              return; // keep the cart drawer open
            }
            // 2) Close triggered by Esc while the Route modal was open. One-shot:
            //    clear the shield as it is consumed, so a quick SECOND Esc (meant
            //    to close the drawer) is NOT also suppressed. Without this, the
            //    shield would linger and swallow the next Esc, leaving the drawer
            //    open while the theme's Predictive Search still strips
            //    'js-drawer-open' -> the white/backdrop-less drawer again.
            if (!evt && Date.now() < shieldUntil) {
              shieldUntil = 0;
              return; // keep the cart drawer open
            }
          }
        } catch (err) { /* fall through to original on any issue */ }
        return originalClose.apply(this, arguments);
      };

      proto.__routeModalPatch = true;
      return true;
    } catch (err) {
      return false; // leave theme behavior unchanged
    }
  }

  // theme.Drawers is usually defined by the time theme assets run, but guard for
  // load-order by retrying briefly if it isn't ready yet.
  if (!patch()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (patch() || ++tries > 100) clearInterval(iv); // up to ~5s
    }, 50);
  }
})();
/* End Route Code */